"use client";

import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { SLOT_TRANSITION } from "./RotationalCarousel.const";
import type { RotationalCarouselItemProps } from "./RotationalCarousel.types";
import {
  circularOffset,
  mergeRefs,
  slotStyle,
} from "./RotationalCarousel.utils";
import { useRotationalCarousel } from "./RotationalCarouselContext";

/**
 * A single slide within the carousel. Each item handles three concerns:
 * @param index - Zero-based position of this item in the full item list.
 *   Must be unique and stable; it is the primary input to `circularOffset`.
 *
 * Must be placed directly inside `<RotationalCarouselContent>`.
 */
const RotationalCarouselItem = forwardRef<
  HTMLDivElement,
  RotationalCarouselItemProps
>(({ index, children, className }, forwardedRef) => {
  const {
    activeIndex,
    totalItems,
    halfWidth,
    sideScale,
    sideOffset,
    setCardDimensions,
  } = useRotationalCarousel();

  // Circular distance from this item to the active item.
  // Negative = left of center, positive = right, 0 = center.
  const offset = circularOffset(index, activeIndex, totalItems);

  // measureRef is always attached to this item's DOM node so useLayoutEffect
  // and ResizeObserver can read its bounding rect.
  const measureRef = useRef<HTMLDivElement>(null);

  // Merge the consumer's forwarded ref with our internal measureRef so both
  // point to the same DOM node. Recomputed only when forwardedRef changes.
  const ref = useMemo(
    () => mergeRefs<HTMLDivElement>(forwardedRef, measureRef),
    [forwardedRef],
  );

  // Sync dimensions before the browser paints to avoid a wrong-position first
  // frame. The carousel content container gets the correct height before the user
  // sees it.
  useLayoutEffect(() => {
    if (offset !== 0 || !measureRef.current) return;
    const { width, height } = measureRef.current.getBoundingClientRect();
    setCardDimensions(width, height);
  }, [offset, setCardDimensions]);

  // Keep dimensions current if the card resizes after the initial mount
  // (e.g. viewport resize, font load, dynamic content).
  useEffect(() => {
    if (offset !== 0 || !measureRef.current) return;
    const el = measureRef.current;
    const observer = new ResizeObserver(([entry]) => {
      // Use borderBoxSize instead of getBoundingClientRect() — the latter
      // includes the CSS scale() transform, which may be mid-transition when
      // ResizeObserver first fires after a navigation, producing a wrong
      // halfWidth and shifting the carousel off-center.
      const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0];
      setCardDimensions(width, height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [offset, setCardDimensions]);

  // First render (halfWidth === 0, dimensions not yet known):
  // Only the center card is rendered, positioned off-screen and invisible,
  // so useLayoutEffect can read its bounding rect before paint.
  //
  // After first measurement (halfWidth > 0):
  // All items within ±2 slots are rendered with their computed transforms.
  if (halfWidth === 0) {
    if (offset !== 0) return null;
    return (
      <div
        ref={ref}
        className={cn(
          "tw-absolute tw-top-0 tw-left-1/2 tw-invisible",
          className,
        )}
      >
        {children}
      </div>
    );
  }

  // Cull items beyond the ±2 buffer — they are never visible and would
  // accumulate unnecessary DOM nodes for long lists.
  if (Math.abs(offset) > 2) return null;

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${index + 1} of ${totalItems}`}
      aria-hidden={Math.abs(offset) > 1}
      className={cn("tw-absolute tw-top-0 tw-left-1/2", className)}
      style={{
        ...slotStyle(offset, halfWidth, sideScale, sideOffset),
        transition: SLOT_TRANSITION,
      }}
    >
      {children}
    </div>
  );
});

RotationalCarouselItem.displayName = "RotationalCarouselItem";

export { RotationalCarouselItem };
